import { api } from '@/lib/api'
import type {
  PoolResponse,
  PlanificarResponse,
  EnviarResponse,
  CapacitacionDetalle,
  ProgramaFiltros,
  ProgramaResponse,
  ProgramasResponse,
  CrearProgramaPayload,
  CrearProgramaResponse,
  CrearVersionProgramaResponse,
  EliminarProgramaResponse,
  EnviarTematicaAhoraResponse,
  DestinatarioPersona,
  DestinatariosOpciones,
  ProgramarEnviosPayload,
  ProgramarEnviosResponse,
  AsignacionesFiltros,
  AsignacionesKpis,
  SidebarBadges,
  CoberturaPersonaResponse,
  TematicaDestinatarioRef,
  TematicaDestinatarioTipo,
} from '@/types/capacitaciones-ia'

/** Query string compartida por G1 (listado) y G2 (KPIs) — mismos filtros,
 * `page` sólo aplica al listado (design #1399: numerador y denominador del
 * MISMO conjunto filtrado). */
function construirQueryAsignaciones(filtros: AsignacionesFiltros): string {
  const params = new URLSearchParams()
  if (filtros.trabajador) params.set('trabajador', filtros.trabajador)
  if (filtros.programa_id !== undefined) params.set('programa_id', String(filtros.programa_id))
  if (filtros.capacitacion_planificada_id !== undefined) {
    params.set('capacitacion_planificada_id', String(filtros.capacitacion_planificada_id))
  }
  if (filtros.area_id) params.set('area_id', filtros.area_id)
  if (filtros.cargo_id !== undefined) params.set('cargo_id', String(filtros.cargo_id))
  if (filtros.resultado) params.set('resultado', filtros.resultado)
  if (filtros.page && filtros.page > 1) params.set('page', String(filtros.page))
  return params.toString()
}

/**
 * `CrearProgramaPayload` → `FormData` (sdd/nuevo-programa-multifuente,
 * design #1663 D8). Los arrays van como claves repetidas
 * (`empresa_documento_ids[]`, `archivos_adhoc[]`, `publico_objetivo_refs[]`)
 * — Laravel las parsea nativo, sin el workaround de stringificar JSON que
 * usa `capacitaciones_ya_generadas` en otro endpoint (ese existe porque
 * `normalizarCampos()` de fc-api devuelve `array<string,string>`; acá no
 * aplica). El contrato plural gana sobre el escalar legado cuando trae
 * contenido, para que un caller futuro (W2b) no tenga que mandar los dos.
 */
function construirFormDataPrograma(payload: CrearProgramaPayload): FormData {
  const form = new FormData()

  if (payload.empresa_documento_ids && payload.empresa_documento_ids.length > 0) {
    payload.empresa_documento_ids.forEach((id) => form.append('empresa_documento_ids[]', String(id)))
  } else if (payload.empresa_documento_id !== undefined) {
    form.append('empresa_documento_id', String(payload.empresa_documento_id))
  }

  payload.archivos_adhoc?.forEach((archivo) => form.append('archivos_adhoc[]', archivo))

  if (payload.nombre !== undefined) form.append('nombre', payload.nombre)
  if (payload.nivel !== undefined) form.append('nivel', payload.nivel)
  if (payload.cantidad_tematicas !== undefined) {
    form.append('cantidad_tematicas', String(payload.cantidad_tematicas))
  }
  if (payload.preguntas_por_tematica !== undefined) {
    form.append('preguntas_por_tematica', String(payload.preguntas_por_tematica))
  }
  // `null` = "sin declarar" en el servidor, que es exactamente lo que pasa
  // cuando el campo está AUSENTE (`sometimes|nullable`) — omitirlo en vez de
  // mandar el string "null" logra el mismo resultado.
  if (payload.publico_objetivo_tipo !== undefined && payload.publico_objetivo_tipo !== null) {
    form.append('publico_objetivo_tipo', payload.publico_objetivo_tipo)
  }
  payload.publico_objetivo_refs?.forEach((ref) => form.append('publico_objetivo_refs[]', String(ref)))

  return form
}

export const capacitacionesIaApi = {
  getPool: (empresaDocumentoId: number): Promise<PoolResponse> =>
    api.get<PoolResponse>(
      `/me/capacitaciones-ia/documentos/${empresaDocumentoId}/capacitaciones`
    ),

  /** Programa de Capacitaciones (Fase B1) — listado transversal con filtros.
   * `generada` viaja como 1/0 porque `false` es un filtro válido y debe
   * distinguirse de "sin filtro". */
  getPrograma: (filtros: ProgramaFiltros = {}): Promise<ProgramaResponse> => {
    const params = new URLSearchParams()
    if (filtros.desde) params.set('desde', filtros.desde)
    if (filtros.hasta) params.set('hasta', filtros.hasta)
    if (filtros.empresa_documento_id !== undefined) {
      params.set('empresa_documento_id', String(filtros.empresa_documento_id))
    }
    if (filtros.generada !== undefined) params.set('generada', filtros.generada ? '1' : '0')
    if (filtros.page && filtros.page > 1) params.set('page', String(filtros.page))

    const qs = params.toString()
    return api.get<ProgramaResponse>(
      `/me/capacitaciones-ia/programa${qs ? `?${qs}` : ''}`
    )
  },

  planificar: (empresaDocumentoId: number): Promise<PlanificarResponse> =>
    api.post<PlanificarResponse>(
      `/me/capacitaciones-ia/documentos/${empresaDocumentoId}/planificar`,
      {}
    ),

  /** Dispara una temática que NUNCA se programó: sale hoy y no toca el
   * calendario, porque no hay día reservado que liberar.
   *
   * `alcance` es opcional y sólo tiene sentido acá: una temática sin
   * programar tampoco tiene audiencia guardada, así que elegirla es parte
   * del envío y el backend la persiste antes de disparar. Omitirlo deja el
   * comportamiento histórico (lo que la temática ya tuviera, o toda la
   * empresa). `enviarTematicaAhora()` no lo acepta: ahí la audiencia se
   * congeló al programar la serie. */
  enviar: (
    capacitacionPlanificadaId: number,
    alcance?: { destinatario_tipo: TematicaDestinatarioTipo; destinatario_refs: TematicaDestinatarioRef[] }
  ): Promise<EnviarResponse> =>
    api.post<EnviarResponse>(
      `/me/capacitaciones-ia/capacitaciones/${capacitacionPlanificadaId}/enviar`,
      alcance ?? {}
    ),

  getDetalle: (capacitacionPlanificadaId: number): Promise<CapacitacionDetalle> =>
    api.get<CapacitacionDetalle>(
      `/me/capacitaciones-ia/capacitaciones/${capacitacionPlanificadaId}/detalle`
    ),

  /** Programas agrupados con sus temáticas (Ola 5, Pantalla 2 — fila expandible). */
  getProgramas: (): Promise<ProgramasResponse> =>
    api.get<ProgramasResponse>('/me/capacitaciones-ia/programas'),

  /** `POST /programas` es multipart (sdd/nuevo-programa-multifuente, design
   * #1663 D8) — `api.postForm()` nunca fija `Content-Type`: el navegador
   * genera el boundary del multipart solo, y forzar el header a mano rompe
   * el parseo en el servidor (se ve como un 422, no como un error de
   * encoding). El contrato plural (`empresa_documento_ids[]`/
   * `archivos_adhoc[]`) tiene prioridad sobre el escalar legado cuando viene
   * con contenido — el drawer de hoy (antes de W2b) sólo manda el escalar. */
  crearPrograma: (payload: CrearProgramaPayload): Promise<CrearProgramaResponse> =>
    api.postForm<CrearProgramaResponse>('/me/capacitaciones-ia/programas', construirFormDataPrograma(payload)),

  crearVersionPrograma: (programaId: number): Promise<CrearVersionProgramaResponse> =>
    api.post<CrearVersionProgramaResponse>(
      `/me/capacitaciones-ia/programas/${programaId}/versiones`,
      {}
    ),

  /** Único camino de recuperación de un Programa `fallido` (D5, WU7) — sin
   * retry: se borra y se empieza de nuevo desde "Nuevo programa". El
   * servidor rechaza con 422 cualquier programa que no esté fallido. */
  eliminarPrograma: (programaId: number): Promise<EliminarProgramaResponse> =>
    api.delete<EliminarProgramaResponse>(`/me/capacitaciones-ia/programas/${programaId}`),

  /** Saca una temática del programa mientras no haya salido a terreno. El
   * servidor responde 422 si ya está congelada o si es la única del programa
   * — los dos guards viven allá, acá sólo se ocultan los botones que
   * sabemos que fallarían. */
  eliminarTematica: (tematicaId: number): Promise<EliminarProgramaResponse> =>
    api.delete<EliminarProgramaResponse>(`/me/capacitaciones-ia/tematicas/${tematicaId}`),

  /* `PUT .../tematicas/{id}` y `POST .../tematicas/{id}/deshacer-edicion`
   * siguen existiendo en la API, pero el cliente web ya no los llama: editar
   * el contenido de una temática salió del alcance de la pantalla. Si vuelve,
   * se reponen contra el contrato del backend, que no cambió. */

  /** Adelanta UNA temática ya programada: genera con IA, manda el correo y
   * le quita el día que tenía reservado en el Plan de envíos.
   *
   * NO es `enviar()`: aquél dispara una temática del pool (sin programación)
   * y no toca el calendario. Éste exige `fecha_programada` — responde 422 sin
   * ella — porque reprogramar es justamente lo que agrega. */
  enviarTematicaAhora: (tematicaId: number): Promise<EnviarTematicaAhoraResponse> =>
    api.post<EnviarTematicaAhoraResponse>(
      `/me/capacitaciones-ia/tematicas/${tematicaId}/enviar-ahora`,
      {}
    ),

  /** Opciones de destinatarios (B1) — dotación real + cargos/áreas con su
   * conteo, para los 4 alcances del drawer "Programar envíos" (E3). */
  getDestinatariosOpciones: (): Promise<DestinatariosOpciones> =>
    api.get<DestinatariosOpciones>('/me/capacitaciones-ia/destinatarios/opciones'),

  /** Trabajadores elegibles para el selector "Trabajador específico".
   *
   * NO usar `/personas?all=1` acá: ese es el listado de RRHH e incluye
   * inactivos a propósito, así que el picker ofrecía gente que el envío
   * después descartaba en silencio. Este endpoint sale del mismo service que
   * resuelve la audiencia real. */
  getDestinatariosPersonas: (): Promise<{ data: DestinatarioPersona[] }> =>
    api.get<{ data: DestinatarioPersona[] }>('/me/capacitaciones-ia/destinatarios/personas'),

  /** Programar envíos (E1) — modo manual dispara ya, modo automática
   * materializa `fecha_programada` por cadencia. */
  programarEnvios: (
    programaId: number,
    payload: ProgramarEnviosPayload
  ): Promise<ProgramarEnviosResponse> =>
    api.post<ProgramarEnviosResponse>(
      `/me/capacitaciones-ia/programas/${programaId}/programar-envios`,
      payload
    ),

  /** KPIs del encabezado del listado fusionado (G2, sobrevive a la fusión —
   * sdd/capacitaciones-pantalla-unica WU4/WU5). Mismos filtros que
   * antes usaba Pantalla 4 "Avance y Resultados" (retirada); sin `page`, es
   * un agregado. Decisión #1394: nunca expone nota_promedio. */
  getAsignacionesKpis: (filtros: Omit<AsignacionesFiltros, 'page'> = {}): Promise<AsignacionesKpis> => {
    const qs = construirQueryAsignaciones(filtros)
    return api.get<AsignacionesKpis>(`/me/capacitaciones-ia/asignaciones/kpis${qs ? `?${qs}` : ''}`)
  },

  // `enviarRecordatorios()` (H1) se eliminó: su único consumidor era
  // `AvanceResultados.tsx`, retirada por la fusión (WU4). El endpoint
  // `POST /me/capacitaciones-ia/recordatorios` y el job siguen vivos en el
  // backend (design D8 — sin cron que los dispare, sólo quedan dormidos);
  // sólo se borra el método muerto del cliente.

  /** Badges del sidebar (I1) — un solo request para los 4 contadores de
   * EMPRESA Y CONTRATOS. Se llama en cada navegación (ver useSidebarBadges),
   * por eso el backend responde con conteos, nunca listados. */
  getBadges: (): Promise<SidebarBadges> =>
    api.get<SidebarBadges>('/me/capacitaciones-ia/badges'),

  /** Cobertura de capacitaciones IA de UN trabajador (MR5 — Ficha del
   * trabajador, pestaña "Capacitaciones"). Por cada Programa que le
   * corresponde según su público objetivo, cuántas temáticas ya enviadas
   * cubrió y con qué desglose. */
  getCoberturaPersona: (personaUuid: string): Promise<CoberturaPersonaResponse> =>
    api.get<CoberturaPersonaResponse>(`/me/capacitaciones-ia/personas/${personaUuid}/cobertura`),
}
